// 利用予約情報に関する共通処理を定義
import { executeGetReservation } from '../../dataAccess/webApi/dao/reservationsDao';
import { fromApiYmd, fromApiYmdHms, formatYmd, formatHm, ElapsedMillisecond } from '../../utils/dateUtil';
import { combineDecimalParts } from '../../utils/objectUtil';
import { DecimalType } from '../../utils/typeUtil';
import { splitDecimal } from '../../utils/stringUtil';
import { dateToNumber } from '../../utils/objectUtil';
import { PostDecodeMstDto } from '../../dataAccess/webApi/dto/decodeMstDto';
import { Flag, purposeOfUse, useReasonDetailDisplayType, yesNo } from './constant/classification';
import { DecodeMstCategory } from './constant/decodeMst';
import { DOCS_SUBMIT_STATUS } from './constant/commonConstant';
import { getDecodeMstValue } from './getDecodeMstValue';
import { GetReservationDto, ReservationDetailSettingDto } from '../../dataAccess/webApi/dto/reservationsDto';
import { translate } from '../../i18n';
import { facilityInputFields } from '../../dataAccess/webApi/dto/facilitiesDto';
import { executeGetReservationCancelReason } from '../../dataAccess/webApi/dao/decodeMstDao';

// 施設情報
export type FacilityInfoType = {
  facilityId: string
  
  facilityName: string
  /** 利用予約即時予約フラグ 0:不可、1:可 */
  immediatelyReservationFlag: string
  /** 昼食受付可否フラグ 0:受け付けない、1:受け付ける */
  lunchAcceptFlag: string
  /** 給食代徴収管理 */
  lunchInitialSelect: string | null
  /** おやつ受付可否フラグ 0:受け付けない、1:受け付ける */
  snackAcceptFlag: string
  /** おやつ代徴収管理 */
  snackInitialSelect: string | null
  /** 見送り時のキャンセル待ち受付可否フラグ 0:受け付けない、1:受け付ける */
  postponeCancelWaitAcceptFlag: string
  /** 利用目的：「非定型」利用フラグ 0:利用しない、1:利用する */
  useReasonUsageFlagIrregular: string
  /** 「非定型」の詳細事由利用フラグ 0:利用しない、1:利用する */
  useReasonDetailUsageFlagIrregular: string
  /** 「非定型」の詳細事由表示タイプ 1: 利用目的と詳細事由を表示 2: 詳細事由のみ表示 */
  detailDisplayTypeIrregular: string
  /** 利用目的：「緊急」利用フラグ 0:利用しない、1:利用する */
  useReasonUsageFlagEmg: string
  /** 「緊急」の利用詳細目的利用フラグ 0:利用しない、1:利用する */
  useReasonDetailUsageFlagEmg: string
  /** 「緊急」の詳細事由表示タイプ 1: 利用目的と詳細事由を表示 2: 詳細事由のみ表示 */
  detailDisplayTypeEmg: string
  /** 利用目的：「リフレッシュ」利用フラグ 0:利用しない、1:利用する */
  useReasonUsageFlagRefresh: string
  /** 「リフレッシュ」の利用詳細目的利用フラグ 0:利用しない、1:利用する */
  useReasonDetailUsageFlagRefresh: string
  /** 「リフレッシュ」の詳細事由表示タイプ 1: 利用目的と詳細事由を表示 2: 詳細事由のみ表示 */
  detailDisplayTypeRefresh: string
  // 医師連絡票
  medicalDoc: string
  // 施設別予約時項目表示フラグ
  facilityInputFieldsDisplayFlag: string
  // 施設別予約時項目選択肢
  facilityInputFields?: facilityInputFields[]
  // TODO: この部分用の型を定義する
  detailSetting: ReservationDetailSettingDto[]
  // 隔離部屋設定有無
  isolationFlag: boolean
}

// 利用者情報
export type ParentInfoType = {
  name: string
  kana: string
  tel: string
  email: string
  residenceCategory?: string
}

// お子様情報
export type ChildInfoType = {
  name: string
  kana: string
  birthday: string
  gender: string
}

export type SelectType = {
  select: string | null
  stringify?: string
}

export type SelectAndOtherType = SelectType & {
  other: string | null
}

export type DecimalWithStringify = DecimalType & {
  stringify?: string
}

export type DocType = {
  fileName: string | null
  delFlg: string | null
  tempUploadKey: string | null
}

export type MedicalDocType = {
  docs: DocType[]
  hasSubmitted: string
  status?: string
}

export type SymptomType = {
  symptoms: string[]
  other: string | null
  stringify?: string
}

export type DiagonosisType = {
  first: SelectAndOtherType
  second: SelectAndOtherType
  stringify?: string
  stringify2?: string
}

export type MedicineType = {
  rx: string | null
  otc: string | null
}

export type FebrileSeizuresType = string

export type EmergencyAction = {
  action: SelectType
  note: string | null
}

export type EatingMethod = {
  method: SelectType
  detail: SelectType
  milkAmount: DecimalWithStringify
  milkFrequency?: number | null
}

export type AllergyType = {
  flag: string,
  foodFlag: string | null
} 

export type ActionsType = {
  action: SelectType
  sleepMethod: string | null
}

export type ReductionType = {
  reduction: SelectType,
  consentCheck?: string
}

export type AttachedDocType = {
  docs: DocType[]
  status?: string
}

export type hospitalInfoType = {
  date: string | null | Date
  time: string | null | Date
  hospitalName: string | null
}

export type facilityInputDetail = {
  id: string | null
  answer: string
  updateDatetime: string | null
  facilityReservationInputFieldId?: number
  order: number | null
}

// お子様情報＋事項入力画面：お子様別入力データ
export type ChildReservationInfoType = {
  childId: string
  childInfo?: ChildInfoType
  medicalDoc?: MedicalDocType
  symptom?: SymptomType
  diagonosis?: DiagonosisType
  epidemic?: SelectAndOtherType
  medicine?: MedicineType
  temperature?: DecimalWithStringify 
  febrileSeizures?: string | null
  emergencyAction?: EmergencyAction
  height?: DecimalWithStringify
  weight?: DecimalWithStringify
  eatingMethod?: EatingMethod
  allergy?: AllergyType
  eatingAbility?: SelectType
  actions?: ActionsType
  toiletAbility?: SelectType
  considerations?: string | null
  reductionType?: ReductionType
  attachedDoc?: AttachedDocType
  hospitalInfo?: hospitalInfoType
  facilityInputFields?: { [key: string]: facilityInputDetail }
}

// 利用日
export type UsageDatetimeType = {
  usageDate: ElapsedMillisecond
  useFromDatetime: ElapsedMillisecond
  useToDatetime: ElapsedMillisecond
  usageBaseDate: string
  status: string
}

// 登降園時間
export type GoingTimeType = {
  goingToFacilityDate: string
  goingToFacilityTime: string
  goingHomeDate: string
  goingHomeTime: string
}

// 必須事項入力画面：入力データ
export type CommonInput = {
  lunchFlag: string
  snackFlag: string
  postponeCancelWaitFlag: string
  goingTimes: GoingTimeType[]
  formUseReason?: SelectAndOtherType
  preferenceProvisionCertificateFlag?: string
  consentCheck?: string
  citizenNote: string
}

export type ShowMessage = {
  Date: string;
  message: string;
}

/**
 * 指定した受付Noの利用予約情報を返す。
 *
 * @param reservationNo 受付No
 * @returns 利用予約情報
 */
export const getReservation = async (reservationNo: string, decodeMstValue?: ReservationSettingDecodeMst) => {
  const response = await executeGetReservation(reservationNo)
  const {
    usageDate,
    useFromDatetime,
    useToDatetime,
    canUpdate,
    canCancel,
    reservationDatetime,
    reservationChangeDatetime,
    name,
    kana,
    tel,
    email,
    childId,
    childName,
    childKana,
    childBirth,
    childGender,
    hasMedicalDocSubmitted,
    symptoms,
    symptomOther,
    diagonosis,
    diagonosisOther,
    diagonosis2,
    diagonosisOther2,
    epidemic,
    epidemicOther,
    rxMedicineFlag,
    otcMedicineFlag,
    bodyTemperature,
    febrileSeizuresFlag,
    emergencyAction,
    emergencyActionNote,
    height,
    weight,
    eatingMethod,
    eatingMethodDetail,
    milkAmount,
    milkFrequency,
    allergyFlag,
    foodAllergyFlag,
    eatingAbility,
    actions,
    sleepMethod,
    toiletAbility,
    considerations,
    reductionType,
    goingToFacilityDatetime, 
    goingHomeDatetime,
    citizenNote,
    useReason,
    useReasonOther,
    hospitalVisitDatetime,
    hospitalName,
    facilityInputAnswers,
    ...other
  } = response.result

  // 病児病後児病児病後児関連書類
  const medicalDocs = [];
  for ( let i = 1; i <= 5; i++ ) {
    const medicalDocKey = `medicalDoc${i}` as keyof GetReservationDto;
    const fileNameKey = `medicalDoc${i}FileName` as keyof GetReservationDto;
  
    const medicalDoc = response.result[medicalDocKey] as string;
    const fileName = response.result[fileNameKey] as string;
    
    if ( !medicalDoc ) break;
    medicalDocs.push( {
      fileName: fileName,
      delFlg: Flag.OFF,
      tempUploadKey: medicalDoc
    })  
  }

  // 添付書類
  const attachedDocs = [];
  for ( let i = 1; i <= 3; i++ ) {
    const attachedDocKey = `attachedDoc${i}` as unknown as keyof GetReservationDto;
    const fileNameKey = `attachedDoc${i}Name` as unknown as keyof GetReservationDto;
    const attachedDoc = response.result[attachedDocKey] as string;
    const fileName = response.result[fileNameKey] as string;
    
    if ( !attachedDoc ) break;
    attachedDocs.push( {
      fileName: fileName,
      delFlg: Flag.OFF,
      tempUploadKey: attachedDoc
    })  
  }

  const inputFields: { [key: string]: facilityInputDetail } = {};
  if (facilityInputAnswers !== null) {
    facilityInputAnswers.forEach(curr => {
      if (curr.facilityReservationInputFieldId) {
        inputFields[String(curr.facilityReservationInputFieldId)] = {
          facilityReservationInputFieldId: curr.facilityReservationInputFieldId,
          id: curr.id,
          answer: curr.answer,
          updateDatetime: curr.updateDatetime,
          order: null
        };
      }
    });
  }
  
  let childInfoList: Required<ChildReservationInfoType>[] = [{
    childId: childId,
    childInfo: {
      name: childName,
      kana: childKana,
      birthday: dateToNumber(childBirth),
      gender: childGender
    },
    medicalDoc: {
      docs: medicalDocs,
      hasSubmitted: hasMedicalDocSubmitted
    },
    symptom: {
      symptoms: (symptoms) ? symptoms.split(',') : [],
      other: symptomOther?? ''
    },
    diagonosis: {
      first: {
        select: diagonosis,
        other: diagonosisOther?? ''
      },
      second: {
        select: diagonosis2,
        other: diagonosisOther2?? ''
      }
    },
    epidemic: {
      select: epidemic,
      other: epidemicOther?? ''
    },
    medicine: {
      rx: rxMedicineFlag,
      otc: otcMedicineFlag
    },
    temperature: splitDecimal(bodyTemperature),
    febrileSeizures: febrileSeizuresFlag,
    emergencyAction: {
      action: {
        select: emergencyAction
      },
      note: emergencyActionNote?? ''
    },
    height: splitDecimal(height),
    weight: splitDecimal(weight),
    eatingMethod: {
      method: {
        select: eatingMethod
      },
      detail:  {
        select: eatingMethodDetail
      },
      milkAmount: splitDecimal(milkAmount),
      milkFrequency: milkFrequency
    },
    allergy: {
      flag: allergyFlag,
      foodFlag: foodAllergyFlag
    },
    eatingAbility: {
      select: eatingAbility
    },
    actions: {
      action: {
        select: actions
      },
      sleepMethod: sleepMethod?? ''
    },
    toiletAbility: {
      select: toiletAbility
    },
    considerations: considerations,
    reductionType: {
      reduction: {
        select: reductionType
      }
    },
    attachedDoc: {
      docs: attachedDocs,
    },
    hospitalInfo: {
      date: hospitalVisitDatetime !== null ? formatYmd(new Date(hospitalVisitDatetime)) : null,
      time: hospitalVisitDatetime !== null ? formatHm(new Date(hospitalVisitDatetime)) : null,
      hospitalName: hospitalName
    },
    facilityInputFields: inputFields
  }]

  let formUseReason: SelectAndOtherType = {
    select: useReason,
    other: useReasonOther
  }
  
  if ( decodeMstValue ) {
    childInfoList = setStringifyNamesChildInfoList(childInfoList, decodeMstValue);
    formUseReason = {
      ... formUseReason,
      stringify: stringifySelectBox(decodeMstValue.formUseReason, formUseReason.select, formUseReason.other, ["その他"])
    }
  }
  
  // 登降園時間を使用しない設定（事業マスタ）場合は'-'でデータが渡ってくる
  const goingDatetimeConverted = ( goingToFacilityDatetime === '-' )? null: fromApiYmdHms(goingToFacilityDatetime);
  const goingHomeDatetimeConverted = ( goingHomeDatetime === '-' )? null: fromApiYmdHms(goingHomeDatetime);
  
  const useFromDatetimeConverted = fromApiYmdHms(useFromDatetime);
  const useToDatetimeConverted = fromApiYmdHms(useToDatetime);

  return {
    ...other,
    reservationNo,
    usageDate: dateToNumber(fromApiYmd(usageDate)),
    useFromDatetime: dateToNumber(useFromDatetimeConverted),
    useToDatetime: dateToNumber(useToDatetimeConverted),
    parentInfo: {
      name: name,
      kana: kana,
      tel: tel,
      email: email
    },
    childInfoList,
    goingTime: {
      goingToFacilityDate: formatYmd(goingDatetimeConverted? goingDatetimeConverted: useFromDatetimeConverted),
      goingToFacilityTime: formatHm(goingDatetimeConverted? goingDatetimeConverted: useFromDatetimeConverted),
      goingHomeDate: formatYmd(goingHomeDatetimeConverted? goingHomeDatetimeConverted: useToDatetimeConverted),
      goingHomeTime: formatHm(goingHomeDatetimeConverted? goingHomeDatetimeConverted: useToDatetimeConverted)
    },
    formUseReason,
    canUpdate: canUpdate === yesNo.yes,
    canCancel: canCancel === yesNo.yes,
    reservationDatetime: fromApiYmdHms(reservationDatetime),
    reservationChangeDatetime: fromApiYmdHms(reservationChangeDatetime),
    citizenNote: citizenNote?? ''
  }
}

/** 利用予約情報の型 */
export type Reservation = ReturnType<typeof getReservation> extends Promise<infer T> ? T : never

/**
 * チェックボックス項目の内容を文字列化する
 * @param decodeMst デコードマスタデータ
 * @param param 判定値
 * @param otherParam その他入力情報
 * @returns
 */
export const stringifyCheckBox = (decodeMst: PostDecodeMstDto[], param?: string[] | null, otherParam?: string | null) => {
  if (!param && !otherParam) {
    return ''
  }
  const tmpArray = param
  if (!tmpArray) return ''
  const response = [];
  for (const tmpData of tmpArray) {
    const findData: String | undefined = decodeMst?.find((c) => c.code === tmpData)?.name
    if (findData) {
      response.push(findData)
      continue
    }
  }

  if (otherParam) response.push(otherParam) 
  return response.join(', ')
}

/**
 * セレクトボックス項目の内容を文字列化する
 * @param decodeMst デコードマスタデータ
 * @param param 判定値
 * @param otherParam その他入力情報
 * @param otherParamTrigger その他入力情報を使用するトリガーとなる判定値
 * @returns
 */
export const stringifySelectBox = (decodeMst: PostDecodeMstDto[], param?: string | null, otherParam?: string | null, otherParamTrigger?: string[]) => {
  if ( !param ) {
    return ''
  }
  const targetDecodeMstData = decodeMst?.find((c) => c.code === param)
  const name = targetDecodeMstData?.name || '';

  if ((otherParamTrigger?.some((trigger) => name.includes(trigger))) && otherParam) {
    return otherParam?? '';
  }
  
  return name.trimStart();
}

const getDocSubmitStatus = (docs: DocType[]| null | undefined) => {
  const filterdDocs = docs?.filter(( doc ) => doc.fileName && doc.tempUploadKey && doc.delFlg === Flag.OFF);
  if ( docs && filterdDocs && filterdDocs.length > 0 ) return DOCS_SUBMIT_STATUS.UPLOADED;
  return DOCS_SUBMIT_STATUS.UN_SUBMITTED;
}

const getMedicalDocSubmitStatus = (hasMedicalDocSubmitted: string | null | undefined, medicalDocList: DocType[]| null | undefined) => {
  if ( hasMedicalDocSubmitted === Flag.ON ) return DOCS_SUBMIT_STATUS.HANDED;
  return getDocSubmitStatus(medicalDocList);
};

const docSubmitStatusTranslationMap = {
  [DOCS_SUBMIT_STATUS.UN_SUBMITTED]: 'facilityReservation.docsStatus.unsubmitted',
  [DOCS_SUBMIT_STATUS.UPLOADED]: 'facilityReservation.docsStatus.uploaded',
  [DOCS_SUBMIT_STATUS.HANDED]: 'facilityReservation.docsStatus.handed',
};

const getDocSubmitStatusName = (status: typeof DOCS_SUBMIT_STATUS [keyof typeof DOCS_SUBMIT_STATUS]) => {
  return translate(docSubmitStatusTranslationMap[status]);
}

/**
 * デコードマスタ
 */
export type ReservationSettingDecodeMstKey<T> = {
  diagonosis: T
  symptom: T
  epidemic: T
  emergency: T
  eatingMethod: T
  brestFeedingDetail: T
  weaningDetailOptions: T
  eatingAbility: T
  actions: T
  toiletAbility: T
  reduction: T
  formUseReason: T
  todayConditionInputFacilityMeal: T
  defecationCondition: T
}

export const DECODE_MST_INPUT_LIST: ReservationSettingDecodeMstKey<string> = {
  diagonosis: DecodeMstCategory.DIAGNOSIS,
  symptom: DecodeMstCategory.SYMPTOM,
  epidemic: DecodeMstCategory.EPIDEMIC,
  emergency: DecodeMstCategory.EMERGENCY,
  eatingMethod: DecodeMstCategory.EATING_METHOD,
  brestFeedingDetail: DecodeMstCategory.BREST_FEEDING_DETAIL,
  weaningDetailOptions: DecodeMstCategory.WEANING_DETAIL_OPTION,
  eatingAbility: DecodeMstCategory.EATING_ABILITY,
  actions: DecodeMstCategory.ACTION,
  toiletAbility: DecodeMstCategory.TOILET_ABILITY,
  reduction: DecodeMstCategory.REDUCTION,
  formUseReason: DecodeMstCategory.FORM_USE_REASON,
  todayConditionInputFacilityMeal: DecodeMstCategory.TODAY_CONDITION_INPUT_FACILITY_MEAL,
  defecationCondition: DecodeMstCategory.DEFECATION_CONDITION
}

export type ReservationSettingDecodeMst = ReservationSettingDecodeMstKey<PostDecodeMstDto[]>

export const getReservationDecodeMstValue = async () => {
  return await getDecodeMstValue(DECODE_MST_INPUT_LIST)
}

export const setStringifyNamesChildInfoList = <T extends ChildReservationInfoType>(childInfoList: T[], decodeMstValue: ReservationSettingDecodeMst):T[] => {
  return childInfoList.map((childInfo) => {
    const returnChildInfo = { ...childInfo };
    
    if ( childInfo.medicalDoc ) {
      returnChildInfo.medicalDoc = {
        ...childInfo.medicalDoc,
        status: getDocSubmitStatusName(getMedicalDocSubmitStatus(childInfo.medicalDoc.hasSubmitted, childInfo.medicalDoc.docs))
      };
    }

    if ( childInfo.hospitalInfo ) {
      returnChildInfo.hospitalInfo = {
        date: childInfo.hospitalInfo.date,
        time: childInfo.hospitalInfo.time,
        hospitalName: childInfo.hospitalInfo.hospitalName,
      }
    }
    
    if ( childInfo.symptom ) {
      returnChildInfo.symptom = {
        ...childInfo.symptom,
        stringify: stringifyCheckBox(decodeMstValue.symptom, childInfo.symptom.symptoms, childInfo.symptom.other),
      };
    }
    
    if ( childInfo.diagonosis ) {
      returnChildInfo.diagonosis = {
        first: {
          ...childInfo.diagonosis.first,
          stringify: stringifySelectBox(decodeMstValue.diagonosis, childInfo.diagonosis.first.select, childInfo.diagonosis.first.other, ['その他'])
        },
        second: {
          ...childInfo.diagonosis.second,
          stringify: stringifySelectBox(decodeMstValue.diagonosis, childInfo.diagonosis.second.select, childInfo.diagonosis.second.other, ['その他'])
        }
      };
    }
    
    if ( childInfo.epidemic ) {
      returnChildInfo.epidemic = {
        ... childInfo.epidemic,
        stringify: stringifySelectBox(decodeMstValue.epidemic, childInfo.epidemic.select, childInfo.epidemic.other, ['その他'])
      }
    }
    
    if ( childInfo.temperature ) {
      returnChildInfo.temperature = {
        ... childInfo.temperature,
        stringify: combineDecimalParts(childInfo.temperature)?.toString()
      }
    }
    
    if ( childInfo.emergencyAction ) {
      returnChildInfo.emergencyAction = {
        ... childInfo.emergencyAction,
        action: {
          select: childInfo.emergencyAction.action.select,
          stringify: stringifySelectBox(decodeMstValue.emergency, childInfo.emergencyAction.action.select)
        }
        
      }
    }
    
    if ( childInfo.height ) {
      returnChildInfo.height = {
        ... childInfo.height,
        stringify: combineDecimalParts(childInfo.height)?.toString()
      }
    }
    
    if ( childInfo.weight ) {
      returnChildInfo.weight = {
        ... childInfo.weight,
        stringify: combineDecimalParts(childInfo.weight)?.toString()
      }
    }
    
    if ( childInfo.eatingMethod ) {
      returnChildInfo.eatingMethod = {
        ... childInfo.eatingMethod,
        method: {
          select: childInfo.eatingMethod.method.select,
          stringify: stringifySelectBox(decodeMstValue.eatingMethod, childInfo.eatingMethod.method.select)
        }
        
      }
    }
    
    if ( childInfo.eatingAbility ) {
      returnChildInfo.eatingAbility = {
        ... childInfo.eatingAbility,
        stringify: stringifySelectBox(decodeMstValue.eatingAbility, childInfo.eatingAbility.select)
      }
    }
    
    if ( childInfo.actions ) {
      returnChildInfo.actions = {
        ... childInfo.actions,
        action: {
          select: childInfo.actions.action.select,
          stringify: stringifySelectBox(decodeMstValue.actions, childInfo.actions.action.select)
        }
      }
    }
    
    if ( childInfo.toiletAbility ) {
      returnChildInfo.toiletAbility = {
        ... childInfo.toiletAbility,
        stringify: stringifySelectBox(decodeMstValue.toiletAbility, childInfo.toiletAbility.select)
      }
    }
    
    if ( childInfo.considerations ) {
      returnChildInfo.considerations = childInfo.considerations
    }
    
    if ( childInfo.reductionType ) {
      returnChildInfo.reductionType = {
        ... childInfo.reductionType,
        reduction: {
          ... childInfo.reductionType.reduction,
          stringify: stringifySelectBox(decodeMstValue.reduction, childInfo.reductionType.reduction.select)
        },
      }
    }

    if ( childInfo.attachedDoc ) {
      returnChildInfo.attachedDoc = {
        ...childInfo.attachedDoc,
        status: getDocSubmitStatusName(getDocSubmitStatus(childInfo.attachedDoc.docs))
      };
    }

    if ( childInfo.facilityInputFields ) {
            returnChildInfo.facilityInputFields = {
        ...childInfo.facilityInputFields,
      };
    }
    
    return returnChildInfo;
  })
}

/**
 * 確認画面で利用目的を隠すべきか？
 */
export const isUseReasonCategoryHidden = (
  useReasonCategory: string | null,
  facility: {
    detailDisplayTypeIrregular: string
    detailDisplayTypeEmg: string
    detailDisplayTypeRefresh: string
  }
) => {
  switch (useReasonCategory) {
    case purposeOfUse.irregular:
      return facility.detailDisplayTypeIrregular === useReasonDetailDisplayType.onlyDetails
    case purposeOfUse.emergency:
      return facility.detailDisplayTypeEmg === useReasonDetailDisplayType.onlyDetails
    case purposeOfUse.refresh:
      return facility.detailDisplayTypeRefresh === useReasonDetailDisplayType.onlyDetails
    default:
      return false
  }
}

export const getReservationCancelReason = async (facilityId: string, keyDate: string) => {
  const input = {
    facilityId,
    keyDate,
  }
  const reservationCancelReasonRes = await executeGetReservationCancelReason(input)
  return reservationCancelReasonRes.result
}